import { HttpStatusCode } from '@robovision/rvai-utils'
import mitt from 'mitt'
import { defineStore } from 'pinia'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import type { LocationQueryRaw } from 'vue-router'
import { License } from '~domain/License'
import type { LoginData } from '~domain/LoginData'
import { Member } from '~domain/Member'
import {
  dataApi,
  hubApi,
  rvaiAuthApi,
} from '~services/api'
import {
  acceptStatus,
  retryIfUnauthorized,
} from '~utils/api'
import { useLicensing } from './composables/useLicensing'
import { useLicenseStore } from './useLicenseStore'


interface LoginPayload {
  user: Member
}

interface LogoutPayload {
  query?: LocationQueryRaw
  redirectToLogin?: boolean
}

export const EventLogin = Symbol('Login')
export const EventLogout = Symbol('Logout')

export const emitter = mitt<{
  [EventLogin]: LoginPayload,
  [EventLogout]: LogoutPayload,
}>()

export const useSessionStore = defineStore('session', () => {
  const { setLicense } = useLicenseStore()
  const { setLicenseNotificationShown } = useLicensing()

  const state = reactive({
    currentUser: undefined as Member | undefined,
  })

  const actions = {
    async checkHealth () {
      try {
        await Promise.all([
          hubApi.livez.getHeartbeat(),
          dataApi.livez.isLive(),
        ])
      } catch (error) {
        this.resetSession()
        throw error
      }
    },

    async loadSession () {
      try {
        const { data: userData } = await rvaiAuthApi.users.getCurrentUser()
        let license
        try {
          const { data: licenseData } = await rvaiAuthApi.license.getLicense({
            validateStatus: acceptStatus(HttpStatusCode.UNPROCESSABLE_ENTITY),
          })
          license = License.fromResponse(licenseData)
        } catch (error) {
          console.warn('Failed to fetch license')
        }
        this.setSession(Member.fromResponse(userData), license)
      } catch (error) {
        this.resetSession()
        throw error
      }
    },

    async login ({
      password,
      username,
    }: LoginData) {
      await rvaiAuthApi.login.login({
        password,
        username,
      })

      // this retry mechanism is necessary to handle the scenario
      // where the auth service returns a valid response for the login
      // but the jwt token has not been stored into the DB yet,
      // which causes the subsequent calls to fail for a brief period.
      // TODO remove the retry once the issue is fixed in RVAI-9445
      const currentUserData = await retryIfUnauthorized(
        async () => {
          const { data } = await rvaiAuthApi.users.getCurrentUser()
          return data
        },
      )

      // get the license info
      let license
      try {
        const { data: licenseData } = await rvaiAuthApi.license.getLicense()
        license = License.fromResponse(licenseData)
      } catch (error) {
        console.warn('Failed to fetch license')
      }
      this.setSession(Member.fromResponse(currentUserData), license)
    },

    async logout (params?: {
      query?: LocationQueryRaw,
      redirectToLogin?: boolean,
    }) {
      await rvaiAuthApi.logout.logout({
        validateStatus: acceptStatus(HttpStatusCode.UNAUTHORIZED),
      })

      this.resetSession(params)
    },

    onLogin (handler: (payload: LoginPayload) => void): void {
      emitter.on(EventLogin, handler)
    },

    onLogout (handler: (payload: LogoutPayload) => void): void {
      emitter.on(EventLogout, handler)
    },

    resetSession (params: {
      query?: LocationQueryRaw,
      redirectToLogin?: boolean
    } = { redirectToLogin: true }) {
      state.currentUser = undefined
      setLicenseNotificationShown(false)

      emitter.emit(EventLogout, params)
    },

    setSession (user: Member, license?: License) {
      state.currentUser = user

      setLicense(license)

      emitter.emit(EventLogin, {
        user: state.currentUser,
      })
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...actions,
  }
})
